import { useTransaction } from "hooks/useTransactions";
import { isEmpty } from "lodash";
import InfoTag from "pages/echeck/components/InfoTag";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import React, { useState } from "react";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { actions as transferActions } from "reducers/transfers";
import * as i18n from "util/i18n";
import gaEventTracker, { events } from "util/gaEventTracker";
import OtherTransferSummaryBody from "./components/Summary";
import BackofficeTransactionPage from "./components/BackofficeTransactionPage";

const confirmRoute = `/transfers/other/confirm`;

const OtherTransferTransactionPage = ({ isDesktop }) => {
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const formatTransactionTransfer = (data, idTransaction) => ({ ...data, idTransaction });

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        userId,
        status,
        routerState,
    } = useTransaction(
        confirmRoute,
        transferActions.setOtherTransferData,
        transferActions.cleanTransferTicket,
        formatTransactionTransfer,
    );

    const isProgrammed = !isEmpty(transaction?.data?.scheduler);
    const { isFinished, isFlowComplete, isPending, isScheduled, isBackoffice, isCancelled } = status;

    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "transfer.other.summary.title",
        id,
        "OtherTransferTransaction",
    );

    const fromContactPage = !isEmpty(routerState?.beneficiary);
    // eslint-disable-next-line no-nested-ternary
    const backLinkTo = fromContactPage ? "/contactDestination" : isFlowComplete ? "/transfers" : "";

    const isBackofficePending = transaction?.data?.backofficePending;

    let subtitle = "";
    if (isPending && isProgrammed) {
        subtitle = `ticket.status.PENDING.schedule`;
    }
    if (isPending && isBackofficePending === true) {
        subtitle = `ticket.status.PENDING.backoffice`;
    }

    const cancelTransaction = () => {
        const { data, idTransaction } = transaction;

        dispatch(
            transferActions.setOtherTransferData({
                ...data,
                idTransaction,
                cancelTransaction: true,
                userId,
            }),
        );
        dispatch(push(confirmRoute));
    };

    const handleSign = () => {
        gaEventTracker.triggerEvent(events.trans_pagos_firmar());
        return handleSignTransaction();
    };

    const handleDownload = async () => {
        await setShowMoreData(true);
        gaEventTracker.triggerEvent(events.trans_pagos_descargar());
        return handlePrint();
    };

    // render de los botones
    const renderBackToHomeButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={() => dispatch(push("/desktop"))}
                label="forms.transaction.ticket.actions.backToHome"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderDownloadButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleDownload}
                image="images/figma/icon/download.svg"
                label="global.download"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderCancelButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={cancelTransaction}
                label="lotPayment.cancel"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderSignButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset}>
            <Button
                onClick={handleSign}
                label="forms.signTransaction.link"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    return (
        <>
            <Notification scopeToShow="OtherTransferTransaction" />
            {!isBackoffice && (
                <ResponsiveHeader ticket metaTitle="transfer.other.summary.title" backLinkTo={backLinkTo} />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="transfer.other.summary.title"
                                subTitle={subtitle}
                                infoComponents={
                                    isProgrammed ? (
                                        <InfoTag tagBackground="programmed" message={i18n.get("scheduler.tag")} />
                                    ) : null
                                }
                                status={isPending && isBackofficePending ? "AWAITING" : ""}
                            />
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    {!isBackoffice ? (
                                        <OtherTransferSummaryBody
                                            isTicket
                                            isDesktop={isDesktop}
                                            setShowMoreData={setShowMoreData}
                                            showMoreData={showMoreData}
                                        />
                                    ) : (
                                        <BackofficeTransactionPage
                                            setShowMoreData={setShowMoreData}
                                            showMoreData={showMoreData}
                                            isDesktop={isDesktop}
                                        />
                                    )}
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            {isCancelled && renderBackToHomeButton(false, 4)}
                            {isScheduled && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderCancelButton(true, 0)}
                                </>
                            )}
                            {isFinished && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderDownloadButton(true, 0)}
                                </>
                            )}

                            {isPending &&
                                (showSignButton ? (
                                    <>
                                        {renderCancelButton(false, 2)}
                                        {renderSignButton(true, 0)}
                                    </>
                                ) : (
                                    <>
                                        {renderBackToHomeButton(false, 2)}
                                        {renderCancelButton(true, 0)}
                                    </>
                                ))}

                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

OtherTransferTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default OtherTransferTransactionPage;
